
import { computed, defineComponent, onMounted, reactive, toRaw, watch } from 'vue'

// components
import TheInput from '@/common/components/UI/TheInput/index.vue'
import TheSelect from '@/common/components/UI/TheSelect/index.vue'
import TheButton from '@/common/components/UI/TheButton/index.vue'
import Checkbox from '@/common/components/UI/Checkbox/index.vue'

// composable
import useHelpers from '@/common/composable/useHelpers'

// types
import { PeriodT } from '@/core/types/create_order.types'

// classes
import { DataState } from '@/core/classes/DataState.class'
import { orders } from '@/core/realisations/orders.realisation'

import { formatDateFromBackend } from '@/common/helpers/formatDate'

export default defineComponent({
    components: {
        TheInput,
        TheSelect,
        TheButton,
        Checkbox,
    },
    props: {
        opened: {
            type: Boolean,
            default: false,
        },
        stepNumber: {
            type: Number,
            default: 0,
        },
        disabled: {
            type: Boolean,
            default: false,
        },
        period: {
            type: Number,
            default: null,
        },
        date: {
            type: String,
            default: null,
        },
    },
    emits: ['submit', 'reset', 'update:date', 'update:period'],
    setup(props, { emit }) {
        const { findObjectByValue } = useHelpers()

        const state = reactive({
            is_period_check: false,
        })

        const data_state = reactive({
            periods: new DataState<PeriodT>({}),
        })

        const min_date = computed(() => {
            const date = new Date(Date.now())
            const hours = date.getHours()

            if (hours >= 14) {
                date.setDate(date.getDate() + 1)
            }

            return date.toISOString().split('T')[0]
        })

        const onPeriodSelected = (period_id: number) => {
            emit('update:period', period_id)
        }

        const isTodayAfternoon = computed(() => {
            const current_date = new Date(),
                current_hour = current_date.getHours()
            const current_date_iso = current_date.toISOString().split('T')[0]

            return current_hour >= 10 && props.date === current_date_iso
        })

        const setPeriod = () => {
            if (isTodayAfternoon.value) {
                emit('update:period', 2)
            } else {
                emit('update:period', 3)
            }
        }

        const onDateSelected = (date: string) => {
            emit('update:date', date)
            state.is_period_check = false
            setPeriod()
        }

        const getPeriods = computed(() => {
            if (isTodayAfternoon.value) {
                return data_state.periods.list.filter((period) => period.id === 2)
            }

            return data_state.periods.list
        })

        const loadPeriods = async () => {
            data_state.periods.pagination.current_page = 1

            try {
                data_state.periods.is_loading = true
                const response = await orders.getPeriods()
                data_state.periods.list = response.data

                data_state.periods.pagination.total_pages = 1

                data_state.periods.is_failed = false
                data_state.periods.failure.error_state.is_cancel = false
            } catch (error) {
                data_state.periods.failure.catchError(error)

                if (!data_state.periods.failure.error_state.is_cancel) {
                    data_state.periods.is_failed = true
                }
            } finally {
                if (!data_state.periods.failure.error_state.is_cancel) {
                    data_state.periods.is_loading = false
                }
            }
        }

        const onCheckboxChange = (value: boolean) => {
            if (!value) {
                setPeriod()
            }
        }

        onMounted(loadPeriods)

        const maxDate = computed(() => {
            const date = new Date()
            date.setFullYear(date.getFullYear() + 1)
            return date.toISOString().split('T')[0]
        })

        return {
            state,
            data_state,
            min_date,
            onPeriodSelected,
            onDateSelected,
            loadPeriods,
            onCheckboxChange,
            //
            getPeriods,
            formatDateFromBackend,
            maxDate
        }
    },
})
